/**
 * Plantilla de servicio para el despachador (DEF-ADM-030 v3).
 *
 * El texto que se le comunica al proveedor por WhatsApp, en bloques:
 * cuándo, dónde, quién.
 *
 * Hubo un segundo formato, con etiquetas en mayúsculas, que convivió con este
 * para decidir cuál se quedaba. El PM los comparó pegados en WhatsApp y
 * eligió este, así que el otro se eliminó: la prueba ya se hizo y dejarlo
 * sería mantener código muerto esperando una decisión que ya está tomada.
 *
 * Nada se envía desde acá: el texto va al portapapeles y el despachador pega.
 */

import { fuenteLabelDeViaje } from './fuente'

/**
 * Qué extremo del viaje es el aeropuerto.
 *
 * Sirve para UNA sola cosa: decidir si va la línea del vuelo. Los nombres de
 * origen y destino salen siempre del campo guardado — la constante AEROPUERTO
 * se quitó en la v2 del DEF, porque con Cusco en operación hay dos aeropuertos
 * y la palabra sola no distingue Jorge Chávez de Velasco Astete.
 *
 * El criterio es el que ya usaba RideEditor y con el que el backend calcula la
 * tarifa del proveedor: APTO-* al salir, *-APTO al llegar. Un tipo que no
 * empieza ni termina en APTO -hoy DISTRITAL- no toca el aeropuerto.
 */
export const esLlegada = (tipoViaje) => !!tipoViaje && String(tipoViaje).startsWith('APTO-')
export const esSalida = (tipoViaje) => !!tipoViaje && String(tipoViaje).endsWith('-APTO')
export const esPuntoAPunto = (tipoViaje) => !esLlegada(tipoViaje) && !esSalida(tipoViaje)

// Cómo se llama cada campo obligatorio cuando hay que decirle al despachador
// que falta. En castellano, como lo ve en pantalla.
const NOMBRES_DE_CAMPO = {
  fecha: 'la fecha',
  hora: 'la hora',
  tipo_vehiculo: 'el tipo de vehículo',
  origen: 'el origen',
  destino: 'el destino',
  nombre: 'el nombre del pasajero',
  celular: 'el teléfono del pasajero'
}

const texto = (valor) => String(valor === null || valor === undefined ? '' : valor).trim()

/**
 * "2026-10-19" -> "19/10/2026".
 *
 * Se parte la cadena en vez de usar Date: `fecha` es una columna DATE que
 * llega ya en hora de Lima, y construir un Date la interpreta como UTC y
 * corre el día. Es el mismo error de toISOString() que ya nos costó caro.
 */
const fechaDdMmAaaa = (fecha) => {
  const partes = texto(fecha).split('-')
  if (partes.length !== 3) return ''
  const [aaaa, mm, dd] = partes
  if (!aaaa || !mm || !dd) return ''
  return `${dd.padStart(2, '0')}/${mm.padStart(2, '0')}/${aaaa}`
}

/** "09:00:00" -> "09:00". */
const horaHhMm = (hora) => {
  const limpia = texto(hora)
  return limpia.length >= 5 ? limpia.substring(0, 5) : ''
}

const nombrePasajero = (viaje) =>
  [texto(viaje.nombres), texto(viaje.apellidos)].filter(Boolean).join(' ')

/**
 * El vuelo, tal como está guardado, o '' cuando no corresponde.
 *
 * El modelo tiene num_vuelo y tipo_vuelo, y tipo_vuelo es NACIONAL/INTER, no
 * la aerolínea: no entra. Lo que el despachador escribe en num_vuelo ya trae
 * aerolínea y número ("LA 2367").
 *
 * En un punto a punto no va, aunque esté cargado por error.
 */
const vuelo = (viaje) =>
  esPuntoAPunto(viaje.tipo_viaje) ? '' : texto(viaje.num_vuelo)

/**
 * El encabezado: "SERVICIO DE TAXI{separador}DESPEGAR".
 *
 * El despachador necesita ver de dónde viene la reserva sin abrirla: una de
 * Despegar no se atiende igual que una directa.
 *
 * La etiqueta sale de fuenteLabelDeViaje, el mismo helper que pinta el badge
 * de la columna Fuente, para que la plantilla y la pantalla no puedan decir
 * cosas distintas. En mayúsculas, como el badge.
 *
 * Sin fuente queda "SERVICIO DE TAXI" solo, sin separador colgando.
 */
const encabezado = (viaje, separador) => {
  const fuente = String(fuenteLabelDeViaje(viaje) || '').trim().toUpperCase()
  return fuente ? `SERVICIO DE TAXI${separador}${fuente}` : 'SERVICIO DE TAXI'
}

/**
 * Qué campos obligatorios le faltan a este viaje. Compartida por los dos
 * formatos: una plantilla a medias es peor que ninguna, se pega igual, se
 * envía igual, y el conductor sale a buscar a alguien con un dato menos.
 */
export const camposQueFaltan = (viaje) => {
  if (!viaje) return [NOMBRES_DE_CAMPO.fecha]

  const presentes = {
    fecha: !!fechaDdMmAaaa(viaje.fecha),
    hora: !!horaHhMm(viaje.hora),
    tipo_vehiculo: !!texto(viaje.tipo_vehiculo),
    origen: !!texto(viaje.origen),
    destino: !!texto(viaje.destino),
    nombre: !!nombrePasajero(viaje),
    celular: !!texto(viaje.celular)
  }

  return Object.keys(presentes)
    .filter((campo) => !presentes[campo])
    .map((campo) => NOMBRES_DE_CAMPO[campo])
}

/** "falta el teléfono del pasajero" / "faltan la hora y el origen". */
export const motivoDeNoPoder = (faltantes) => {
  if (!faltantes.length) return ''
  const verbo = faltantes.length === 1 ? 'falta' : 'faltan'
  const lista = faltantes.length === 1
    ? faltantes[0]
    : `${faltantes.slice(0, -1).join(', ')} y ${faltantes[faltantes.length - 1]}`
  return `No se puede armar la plantilla: ${verbo} ${lista}.`
}

// Las observaciones son una nota del despachador, no una alerta: 📝 lo dice
// sin dramatizar. Se descartó ⚠️, que le pone urgencia a un "llamar al
// llegar", y 💬, que se lee como un mensaje de alguien.
const EMOJI_OBSERVACIONES = '📝'

/**
 * El equipaje: "{N} PAX · {M} maletas". Vacío si no hay ni pasajeros ni
 * maletas, y entonces la línea no sale.
 *
 * Las observaciones NO van acá: tienen su propia línea al final.
 */
const equipaje = (viaje) => {
  const pax = Number(viaje.cant_pax) || 0
  const maletas = Number(viaje.cant_maletas) || 0
  if (pax <= 0 && maletas <= 0) return ''
  return `${pax} PAX · ${maletas} maletas`
}

/**
 * El teléfono con el + delante, para que WhatsApp lo reconozca como número y
 * el conductor pueda tocarlo y escribirle al pasajero.
 *
 * En la base está guardado sin el símbolo (5511992383830). Esto es solo
 * presentación: el dato guardado no se toca. Si ya viene con +, no se duplica.
 */
const telefono = (viaje) => {
  const numero = texto(viaje.celular)
  if (!numero) return ''
  return numero.startsWith('+') ? numero : `+${numero}`
}

/**
 * El formato de bloques con emojis: cuándo, dónde, quién.
 *
 * Sin línea en blanco dentro de cada bloque: el emoji ya hace de etiqueta. Los
 * emojis no son markdown y WhatsApp no los interpreta, solo los renderiza.
 * El verde y el rojo son los mismos de la columna Ruta del portal.
 *
 * Devuelve null si falta algún campo obligatorio, igual que el clásico.
 */
export const construirPlantilla = (viaje) => {
  if (camposQueFaltan(viaje).length) return null

  const cuando = [
    `📅 ${fechaDdMmAaaa(viaje.fecha)}`,
    `🕐 ${horaHhMm(viaje.hora)}`,
    `🚗 ${texto(viaje.tipo_vehiculo).toUpperCase()}`
  ]

  const donde = [
    `🟢 ${texto(viaje.origen)}`,
    `🔴 ${texto(viaje.destino)}`
  ]
  const numeroDeVuelo = vuelo(viaje)
  if (numeroDeVuelo) donde.push(`✈️ ${numeroDeVuelo}`)

  const quien = [
    `👤 ${nombrePasajero(viaje)}`,
    `📞 ${telefono(viaje)}`
  ]
  const lineaEquipaje = equipaje(viaje)
  if (lineaEquipaje) quien.push(`🧳 ${lineaEquipaje}`)

  const bloques = [
    encabezado(viaje, ' · '),
    cuando.join('\n'),
    donde.join('\n'),
    quien.join('\n')
  ]

  // Las observaciones van al final, en su propia línea y separadas del bloque
  // de la persona: son algo del servicio, no un dato del pasajero. Si el campo
  // está vacío no sale nada, ni la línea ni un bloque en blanco.
  const observaciones = texto(viaje.observaciones)
  if (observaciones) bloques.push(`${EMOJI_OBSERVACIONES} ${observaciones}`)

  return bloques.join('\n\n')
}

// ─── Varias juntas, y el portapapeles ───────────────────────────────────────

/**
 * Varias plantillas, separadas por una línea en blanco, en el orden recibido.
 * Los viajes a los que les falta algo se omiten y se devuelven aparte.
 */
export const construirVariasPlantillas = (viajes) => {
  const textos = []
  const omitidos = []
  ;(viajes || []).forEach((viaje) => {
    const plantilla = construirPlantilla(viaje)
    if (plantilla) textos.push(plantilla)
    else omitidos.push(viaje)
  })
  return { texto: textos.join('\n\n'), omitidos }
}

/**
 * Deja el texto en el portapapeles. Devuelve true si lo logró.
 *
 * navigator.clipboard solo existe en contexto seguro (HTTPS o localhost). El
 * portal corre en HTTPS en los dos entornos, pero en desarrollo local por IP
 * no, y ahí fallaría en silencio: por eso queda el respaldo clásico.
 */
export const copiarAlPortapapeles = async (contenido) => {
  if (!contenido) return false

  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(contenido)
      return true
    } catch (e) {
      // Sigue al respaldo: el permiso puede estar denegado aunque el contexto
      // sea seguro.
    }
  }

  try {
    const area = document.createElement('textarea')
    area.value = contenido
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.top = '-9999px'
    document.body.appendChild(area)
    area.select()
    const copiado = document.execCommand('copy')
    document.body.removeChild(area)
    return copiado
  } catch (e) {
    return false
  }
}
